import {Text} from 'epic-fortnite-react';
import {SlickWrapper} from 'epic-react-common';
import React from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';

import {Device} from '../../../constants';

import FeatureCarouselItem from './FeatureCarouselItem';
import FeatureHeroCarouselContainer from './FeatureHeroCarouselContainer';
import FeatureIcon from './FeatureIcon';
import HeroBackground from './HeroBackground';
import ScrollContainer from './ScrollContainer';
import ThumbContainer from './ThumbContainer';
import {HeroSlide} from './Types';

const StyledFadedTransitionOverlay = styled.div<{color?: string}>`
  position: absolute;
  bottom: 0;
  height: 300px;
  width: 100%;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    ${({color}) => color ?? '#fff'} 100%
  );
  z-index: 1;
`;

const StyledHeroNameWrapper = styled.div`
  width: 100%;
  text-align: center;
  position: absolute;
  bottom: 275px;
  display: flex;
  justify-content: center;
  z-index: 2;

  @media screen and ${Device.tablet} {
    bottom: 235px;
  }

  @media screen and ${Device.mobileL} {
    bottom: 210px;
  }
`;

interface Props {
  items: HeroSlide[];
  setCurrentSlide: (value: number) => void;
  activeSlide: number;
  isVisible: boolean;
  mobile: boolean;
  tablet: boolean;
  seasonKey: string;
}

interface SlickWrapper extends Slider {
  slickGoTo: (value: number) => void;
}

const FeaturedCarousel = ({
  seasonKey,
  items,
  activeSlide,
  setCurrentSlide,
  isVisible,
  tablet,
  mobile,
}: Props) => {
  const [sliderRef, setSliderRef] = React.useState<SlickWrapper | null>(null);
  const [currentCharacter, setCurrentCharacter] = React.useState(0);

  const sliderSettings = React.useMemo(() => {
    return {
      dots: false,
      arrows: false,
      infinite: false,
      swipeToSlide: false,
      useCSS: false,
      useTransform: false,
      touchMove: false,
      swipe: false,
      slidesToShow: 1,
      afterChange: (current) => setCurrentSlide(current),
    };
  }, [setCurrentSlide]);

  const goToSlide = React.useCallback(
    (index) => {
      setCurrentCharacter(index);
      if (index === items.length) {
        sliderRef?.slickGoTo(0);
        return;
      }
      if (activeSlide !== index) {
        sliderRef?.slickGoTo(index);
      }
    },
    [sliderRef, activeSlide]
  );

  return (
    <FeatureHeroCarouselContainer>
      <HeroBackground
        seasonKey={seasonKey}
        mobile={mobile}
        tablet={tablet}
        items={items}
        activeSlide={activeSlide}
        isVisible={isVisible}
      />
      <SlickWrapper ref={setSliderRef} options={sliderSettings}>
        {items.map((item, index) => {
          return (
            <FeatureCarouselItem
              isVisible={isVisible}
              active={index === activeSlide}
              key={`heroSlide-${item.key}`}
              data={item}
              mobile={mobile}
              tablet={tablet}
              index={index}
              goToSlide={goToSlide}
            />
          );
        })}
      </SlickWrapper>
      <StyledHeroNameWrapper>
        <Text
          align="center"
          color="#fff"
          lineLength="sm"
          size={mobile ? 'xl' : '3xl'}
          transform="uppercase">
          {items[currentCharacter]?.name || ''}
        </Text>
      </StyledHeroNameWrapper>
      <StyledFadedTransitionOverlay color="#163896" />
      <ThumbContainer>
        <ScrollContainer>
          {items.map((item, index) => {
            return (
              <FeatureIcon
                goToSlide={goToSlide}
                index={index}
                active={index === activeSlide}
                key={`heroThumb-${item.key}`}
                data={item}
              />
            );
          })}
        </ScrollContainer>
      </ThumbContainer>
    </FeatureHeroCarouselContainer>
  );
};

export default FeaturedCarousel;
